import { Box, Button, CircularProgress, Divider, Grid2, Paper, Typography } from "@mui/material";
import { FC } from "react";
import { Logo } from "../../assets/images";
import styles from "./SalesExperienceYearlyOffer.module.scss";

interface IProps {
  handleYearly: () => void;
  handleMonthly: () => void;
  isLoading: {
    accept: boolean;
    decline: boolean;
  };
}

const SalesExperienceYearlyOffer: FC<IProps> = ({ handleYearly, handleMonthly, isLoading }) => {
  
  // Bonus cards data
  const bonusCards = [
    {
      title: "Sales Experience Community Access",
      value: "Value: $99/month",
      description:
        "One year of expert support, community access, and The Sales Experience digital book.",
      icon: "/icons/salesExperiance.webp",
      points: [
        "Private member community",
        "Monthly live training workshops",
        "Q&A sessions with experts",
        "Networking opportunities",
        "Case study deep-dives",
      ],
    },
    {
      title: "Advanced Webinar Training",
      value: "Value: $1,900+",
      description:
        "Elite Webinar Mastery plus a free all-access pass to every monthly paid webinar.",
      icon: "/icons/advanceWebinar.webp",
      points: [
        "Pre-built funnel templates",
        "Registration to thank-you pages",
        "Email sequences included",
        "Optimized for conversions",
        "One-click deployment",
      ],
    },
    {
      title: "EasyCRM — Full Sales Engine",
      value: "Value: $1,200+",
      description:
        "Lead tracking, automation, and full pipeline visibility built directly into your account.",
      icon: "/icons/easyCrm.webp",
      points: [
        "Lead management & tracking",
        "Sales pipeline visualization",
        "Contact management tools",
        "Deal tracking & follow-ups",
        "Integration with webinars",
      ],
    },
    {
      title: "Fast-Action Bonuses",
      value: "Value: ~$2,000",
      description:
        "Hands-on support to optimize your funnel, refine your offer, and handle platform migration.",
      icon: "/icons/fastAction.webp",
      points: [
        "Hands-on funnel optimization",
        "Refine your offer & messaging",
        "Platform migration support",
        "Customizable launch strategy",
        "Save hours of preparation time",
      ],
    },
  ];

  return (
    <>
      <Paper className={styles.wrapper} elevation={4}>
        {/* 0. Brand Logo */}
        <Box className={styles.logoWrapper}>
          <img src={Logo} alt="EasyWebinar logo" className={styles.logo} />
        </Box>

        {/* 1. Hero Header */}
        <Box className={styles.alertHeader}>
          <Typography
            variant="h5"
            className={styles.specialOfferTag}
            sx={{ fontWeight: 900 }}
          >
            <span className={styles.specialOfferIcon}>⏳</span> SPECIAL ONE-TIME
            OFFER
          </Typography>

          <Typography className={styles.alertText} sx={{ fontWeight: 800 }}>
            Wait! Upgrade to Annual &amp; Save $240
          </Typography>
        </Box>

        {/* 2. Progress & Savings Callout */}
        <Box className={styles.savingsBox}>
          <Box className={styles.progressBarTrack}>
            <Box className={styles.progressBarFill}>
              <Typography className={styles.progressLabel}>60%</Typography>
            </Box>
          </Box>

          <Typography
            className={styles.savingsHeadline}
            sx={{ fontWeight: 700 }}
          >
            Plus Get $2,985 Worth of Exclusive Bonuses FREE
          </Typography>

          <Typography className={styles.savingsText} sx={{ fontWeight: 400 }}>
            Lock in your price, save $240/year, and get instant access to our
            premium tools and training valued at $2,985.
          </Typography>
        </Box>

        {/* 3. Pricing Comparison */}
        <Box className={styles.pricingSection}>
          <Typography className={styles.pricingTitle} sx={{ fontWeight: 800 }}>
            Monthly vs Annual - The Smart Choice
          </Typography>

          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box className={styles.priceCard}>
                <Typography
                  className={styles.planLabel}
                  sx={{ fontWeight: 700 }}
                >
                  Your Current Plan
                </Typography>
                <Typography
                  className={styles.planPrice}
                  sx={{ fontWeight: 700 }}
                >
                  $116<span>/mo</span>
                </Typography>
                <Typography
                  className={styles.planSubLabel}
                  sx={{ fontWeight: 400 }}
                >
                  Monthly Billing
                </Typography>
                <Typography
                  className={styles.planTotal}
                  sx={{ fontWeight: 400 }}
                >
                  = $1,392/year
                </Typography>
              </Box>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box className={`${styles.priceCard} ${styles.recommended}`}>
                <Typography
                  className={styles.planLabel}
                  sx={{ fontWeight: 700 }}
                >
                  Upgrade to Annual
                </Typography>
                <Typography
                  className={styles.planPrice}
                  sx={{ fontWeight: 700 }}
                >
                  $96<span>/mo</span>
                </Typography>
                <Typography
                  className={styles.planSubLabel}
                  sx={{ fontWeight: 400 }}
                >
                  SAVE $240/year
                </Typography>
                <Typography
                  className={styles.planTotal}
                  sx={{ fontWeight: 400 }}
                >
                  = $1,152/year
                </Typography>
              </Box>
            </Grid2>
          </Grid2>

          <Typography className={styles.bonusInline} sx={{ fontWeight: 600 }}>
            <span className={styles.bonusIcon}>🎁</span> Plus $2,985 in
            Exclusive Bonuses (See Below)
          </Typography>
        </Box>

        <Divider />

        {/* 4. Bonus Stack */}
        <Box className={styles.bonusSection}>
          <Typography className={styles.sectionTitle} sx={{ fontWeight: 800 }}>
            Your Exclusive Annual Plan Bonuses
          </Typography>
          <Typography className={styles.bonusSubtitle} sx={{ fontWeight: 400 }}>
            Upgrade now and unlock these powerful tools and resources instantly.
          </Typography>
          <Typography className={styles.bonusMeta} sx={{ fontWeight: 600 }}>
            Annual Savings — 3 months free
          </Typography>

          <Grid2 container spacing={3}>
            {bonusCards.map((bonus) => (
              <Grid2 key={bonus.title} size={{ xs: 12, md: 6, lg: 3 }}>
                <Box className={styles.bonusCard}>
                  <Box className={styles.bonusIconWrapper}>
                    <img
                      src={bonus.icon}
                      alt={bonus.title}
                      className={styles.bonusIconImage}
                    />
                  </Box>

                  <Typography
                    className={styles.bonusCardTitle}
                    sx={{ fontWeight: 700 }}
                  >
                    {bonus.title}
                  </Typography>

                  <Typography
                    className={styles.bonusValueText}
                    sx={{ fontWeight: 700 }}
                  >
                    {bonus.value}
                  </Typography>

                  <Typography
                    className={styles.bonusDescription}
                    sx={{ fontWeight: 400 }}
                  >
                    {bonus.description}
                  </Typography>

                  <ul className={styles.bonusList}>
                    {bonus.points.map((point) => (
                      <li key={point} className={styles.bonusListItem}>
                        <span className={styles.tickIcon} />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>

        {/* 5. Value Summary Section */}
        <Box className={styles.valueBreakdownSection}>
          <Typography
            className={styles.valueBreakdownTitle}
            sx={{ fontWeight: 800 }}
          >
            Here's What You're Getting Today
          </Typography>

          <Box className={styles.valueCardsRow}>
            <Box className={styles.valueCardBlue}>
              <Typography
                className={styles.valueCardAmount}
                sx={{ fontWeight: 700 }}
              >
                $240
              </Typography>
              <Typography
                className={styles.valueCardLabel}
                sx={{ fontWeight: 400 }}
              >
                Savings on Annual Plan
              </Typography>
            </Box>

            <Box className={styles.valueCardBlue}>
              <Typography
                className={styles.valueCardAmount}
                sx={{ fontWeight: 700 }}
              >
                $2,985
              </Typography>
              <Typography
                className={styles.valueCardLabel}
                sx={{ fontWeight: 400 }}
              >
                In Bonus Tools & Training
              </Typography>
            </Box>

            <Box className={styles.valueCardBlue}>
              <Typography
                className={styles.valueCardAmount}
                sx={{ fontWeight: 700 }}
              >
                $3,225
              </Typography>
              <Typography
                className={styles.valueCardLabel}
                sx={{ fontWeight: 400 }}
              >
                Total Value You Get
              </Typography>
            </Box>
          </Box>

          <Typography
            className={styles.investmentText}
            sx={{ fontWeight: 700 }}
          >
            Your Investment: Just $1,152 (One Time)
          </Typography>

          <Typography
            className={styles.investmentSubtext}
            sx={{ fontWeight: 400 }}
          >
            That's only $96/month - $20/month less than monthly billing
          </Typography>
        </Box>

        {/* 6. Final CTA Card */}
        <Box className={styles.finalCtaCard}>
          <Typography className={styles.finalCtaTitle} sx={{ fontWeight: 800 }}>
            Yes! Upgrade Me to Annual Plan
          </Typography>

          <Typography
            className={styles.finalCtaSubtitle}
            sx={{ fontWeight: 400 }}
          >
            Lock in your price and get all 4 bonuses instantly
          </Typography>

          <Typography
            className={styles.valueCalculation}
            sx={{ fontWeight: 400 }}
          >
            <span className={styles.strikethrough}>
              $1,392 + $2,985 = $4,377 Value
            </span>
          </Typography>

          <Typography className={styles.finalPrice} sx={{ fontWeight: 700 }}>
            $1,152{" "}
            <span className={styles.finalPriceLabel}>one-time payment</span>
          </Typography>

          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={handleYearly}
            disabled={isLoading.accept || isLoading.decline}
            className={styles.upgradeButton}
          >
            {isLoading.accept ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              "UPGRADE TO ANNUAL PLAN"
            )}
          </Button>

          <Box className={styles.benefitsList}>
            <Box className={styles.benefitItem}>
              <span className={styles.checkmark}>✓</span>
              <Typography sx={{ fontWeight: 400 }}>
                Same 7-Day Money-Back Guarantee
              </Typography>
            </Box>
            <Box className={styles.benefitItem}>
              <span className={styles.checkmark}>✓</span>
              <Typography sx={{ fontWeight: 400 }}>
                All Bonuses Activated Immediately
              </Typography>
            </Box>
            <Box className={styles.benefitItem}>
              <span className={styles.checkmark}>✓</span>
              <Typography sx={{ fontWeight: 400 }}>
                Lock In This Price Forever
              </Typography>
            </Box>
            <Box className={styles.benefitItem}>
              <span className={styles.checkmark}>✓</span>
              <Typography sx={{ fontWeight: 400 }}>
                Cancel Anytime (Pro-Rated Refund)
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="text"
            onClick={handleMonthly}
            className={styles.declineButton}
            disabled={isLoading.accept || isLoading.decline}
          >
            {isLoading.decline ? (
              <CircularProgress size={20} sx={{ color: "#2563eb" }} />
            ) : (
              "No thanks, I'll stick with monthly billing and miss out on these bonuses"
            )}
          </Button>
        </Box>
      </Paper>
    </>
  );
};

export default SalesExperienceYearlyOffer;
